import { Component, OnInit, Input, OnChanges, SimpleChanges , DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { RaidItemService, RaidItem, CreateRaidItemDto } from '../../services/raid-item.service';
@Component({
  selector: 'app-raid',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Tag,
    Tooltip,
    Dialog,
    InputText,
    Select,
    DatePicker,
    ConfirmDialog,
  ],
  templateUrl: './raid.component.html',
  styleUrl: './raid.component.scss',
  providers: [MessageService, ConfirmationService]
})
export class RaidComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  @Input() projectId: string = '';

  items: RaidItem[] = [];
  selectedItem: RaidItem | null = null;
  displayItemDialog: boolean = false;
  isEditItemMode: boolean = false;
  identifiedDate: Date | null = null;
  targetDate: Date | null = null;
  dateClosed: Date | null = null;
  loading: boolean = false;
  fieldTouched: Record<string, boolean> = {};

  typeOptions = [
    { label: 'Risk', value: 'Risk' },
    { label: 'Assumption', value: 'Assumption' },
    { label: 'Issue', value: 'Issue' },
    { label: 'Dependency', value: 'Dependency' }
  ];

  impactOptions = [
    { label: 'High', value: 'High' },
    { label: 'Medium', value: 'Medium' },
    { label: 'Low', value: 'Low' }
  ];

  statusOptions = [
    { label: 'Open', value: 'Open' },
    { label: 'Off Track', value: 'Off Track' },
    { label: 'Closed', value: 'Closed' }
  ];

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private raidItemService: RaidItemService
  ) {}

  ngOnInit() {
    if (this.projectId) {
      this.loadItems();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projectId'] && this.projectId) {
      this.loadItems();
    }
  }

  loadItems() {
    if (!this.projectId) {
      return;
    }
    this.loading = true;
    this.raidItemService.getByProjectId(this.projectId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (items) => {
        this.items = items;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading RAID items:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load RAID items. Please try again.'
        });
        this.loading = false;
      }
    });
  }

  showAddItemDialog() {
    if (!this.projectId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Project ID is required to add RAID items.'
      });
      return;
    }
    this.selectedItem = {
      id: '',
      projectId: this.projectId,
      itemNumber: this.generateItemNumber(),
      type: 'Risk',
      description: '',
      identifiedDate: this.formatDateToLocalString(new Date()),
      impact: 'High',
      owner: '',
      targetDate: '',
      status: 'Open',
      mitigationPlan: '',
      dateClosed: '',
      remark: ''
    };
    this.identifiedDate = new Date();
    this.targetDate = null;
    this.dateClosed = null;
    this.fieldTouched = {};
    this.isEditItemMode = false;
    this.displayItemDialog = true;
  }

  showEditItemDialog(item: RaidItem) {
    this.selectedItem = { ...item };
    this.identifiedDate = item.identifiedDate ? this.parseLocalDateString(item.identifiedDate) : null;
    this.targetDate = item.targetDate ? this.parseLocalDateString(item.targetDate) : null;
    this.dateClosed = item.dateClosed ? this.parseLocalDateString(item.dateClosed) : null;
    this.fieldTouched = {};
    this.isEditItemMode = true;
    this.displayItemDialog = true;
  }

  resetItemForm() {
    this.fieldTouched = {};
    if (!this.selectedItem) return;
    if (this.isEditItemMode) {
      const item = this.items.find(i => i.id === this.selectedItem?.id);
      if (item) {
        this.selectedItem = { ...item };
        this.identifiedDate = item.identifiedDate ? this.parseLocalDateString(item.identifiedDate) : null;
        this.targetDate = item.targetDate ? this.parseLocalDateString(item.targetDate) : null;
        this.dateClosed = item.dateClosed ? this.parseLocalDateString(item.dateClosed) : null;
      }
    } else {
      this.selectedItem.itemNumber = this.generateItemNumber();
      this.selectedItem.type = 'Risk';
      this.selectedItem.description = '';
      this.selectedItem.impact = 'High';
      this.selectedItem.owner = '';
      this.selectedItem.status = 'Open';
      this.selectedItem.mitigationPlan = '';
      this.selectedItem.remark = '';
      this.selectedItem.identifiedDate = this.formatDateToLocalString(new Date());
      this.selectedItem.targetDate = undefined;
      this.selectedItem.dateClosed = undefined;
      this.identifiedDate = new Date();
      this.targetDate = null;
      this.dateClosed = null;
    }
  }

  saveItem() {
    this.fieldTouched['description'] = true;
    this.fieldTouched['owner'] = true;

    if (!this.selectedItem || !this.selectedItem.description.trim()) {
      return;
    }

    if (!this.selectedItem.owner.trim()) {
      return;
    }

    if (!this.projectId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Project ID is required.'
      });
      return;
    }

    if (this.identifiedDate) {
      this.selectedItem.identifiedDate = this.formatDateToLocalString(this.identifiedDate);
    }

    if (this.targetDate) {
      this.selectedItem.targetDate = this.formatDateToLocalString(this.targetDate);
    } else {
      this.selectedItem.targetDate = undefined;
    }

    if (this.dateClosed) {
      this.selectedItem.dateClosed = this.formatDateToLocalString(this.dateClosed);
    } else {
      this.selectedItem.dateClosed = undefined;
    }

    if (this.isEditItemMode) {
      // Update existing item
      this.raidItemService.updateItem(this.selectedItem.id, this.selectedItem).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (updatedItem) => {
          const index = this.items.findIndex(i => i.id === updatedItem.id);
          if (index !== -1) {
            this.items[index] = updatedItem;
          }
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'RAID item updated successfully.'
          });
          this.displayItemDialog = false;
          this.selectedItem = null;
        },
        error: (error) => {
          console.error('Error updating RAID item:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update RAID item. Please try again.'
          });
        }
      });
    } else {
      // Create new item
      const createDto: CreateRaidItemDto = {
        projectId: this.projectId,
        itemNumber: this.selectedItem.itemNumber,
        type: this.selectedItem.type,
        description: this.selectedItem.description,
        identifiedDate: this.selectedItem.identifiedDate,
        impact: this.selectedItem.impact,
        owner: this.selectedItem.owner,
        targetDate: this.selectedItem.targetDate,
        status: this.selectedItem.status,
        mitigationPlan: this.selectedItem.mitigationPlan,
        dateClosed: this.selectedItem.dateClosed,
        remark: this.selectedItem.remark
      };

      this.raidItemService.addItem(createDto).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (newItem) => {
          this.items.push(newItem);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'RAID item added successfully.'
          });
          this.displayItemDialog = false;
          this.selectedItem = null;
        },
        error: (error) => {
          console.error('Error adding RAID item:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add RAID item. Please try again.'
          });
        }
      });
    }
  }

  deleteItem(item: RaidItem) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete RAID item ${item.itemNumber ? '#' + item.itemNumber : ''} "${item.description || ''}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.raidItemService.deleteItem(item.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            const index = this.items.findIndex(i => i.id === item.id);
            if (index !== -1) {
              this.items.splice(index, 1);
            }
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'RAID item deleted successfully.'
            });
          },
          error: (error) => {
            console.error('Error deleting RAID item:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete RAID item. Please try again.'
            });
          }
        });
      }
    });
  }

  generateItemNumber(): string {
    if (this.items.length === 0) {
      return '00001';
    }
    const maxId = Math.max(...this.items.map(i => {
      const num = parseInt(i.itemNumber.replace(/\D/g, ''));
      return isNaN(num) ? 0 : num;
    }));
    return String(maxId + 1).padStart(5, '0');
  }

  /** Parse YYYY-MM-DD as local date (avoids timezone shifting to previous/next day). */
  parseLocalDateString(dateStr: string): Date | null {
    const parts = dateStr.trim().split('-');
    if (parts.length !== 3) return null;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    const date = new Date(y, m, d);
    if (isNaN(date.getTime())) return null;
    return date;
  }

  /** Format a Date to YYYY-MM-DD using local date (avoids timezone shifting when saving). */
  formatDateToLocalString(date: Date): string {
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = this.parseLocalDateString(dateString);
    if (!date) return '-';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  getImpactSeverity(impact: string): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (impact) {
      case 'High':
        return 'danger';
      case 'Medium':
        return 'warn';
      case 'Low':
        return 'success';
      default:
        return 'secondary';
    }
  }

  getStatusSeverity(status: string): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'Closed':
        return 'success';
      case 'Off Track':
        return 'warn';
      case 'Open':
        return 'danger';
      default:
        return 'secondary';
    }
  }
}

